import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Truck, CreditCard, Banknote, MapPin, Plus, Check, Building2, Copy, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import CheckoutProgress from '../components/CheckoutProgress';
import Breadcrumbs from '../components/Breadcrumbs';
import EcontDeliverySelector from '../components/EcontDeliverySelector';
import { getPriceData } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MINIMUM_ORDER_AMOUNT = 15.00; // Minimum order in EUR

const SOFIA_CITY_VARIANTS = ['софия', 'sofia', 'sofia city', 'столична община', 'софия град', 'sofia grad'];
const isSofiaCity = (city) => {
    if (!city) return false;
    const normalized = city.toLowerCase().replace('гр.', '').replace('гр ', ' ').split(/\s+/).filter(Boolean).join(' ').trim();
    return SOFIA_CITY_VARIANTS.includes(normalized);
};

const CheckoutPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { cart, clearCart, sessionId, loading: cartLoading } = useCart();
    const { token, user } = useAuth();
    
    const [loading, setLoading] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState('');
    const [showNewAddress, setShowNewAddress] = useState(false);
    const [deliveryType, setDeliveryType] = useState('address');
    const [paymentMethod, setPaymentMethod] = useState('cod');
    const [notes, setNotes] = useState('');
    const [bankDetails, setBankDetails] = useState(null);
    const [customerEmail, setCustomerEmail] = useState('');
    const [errors, setErrors] = useState({});
    
    // Check if user is guest
    const isGuest = !token || !user;
    
    const [newAddress, setNewAddress] = useState({
        name: '',
        phone: '',
        city: '',
        address: '',
        postal_code: '',
        econt_office: ''
    });
    
    const [econtDelivery, setEcontDelivery] = useState(null);
    const [shippingCost, setShippingCost] = useState(0);

    // Notify the customer that a cancelled payment keeps the cart intact
    useEffect(() => {
        if (searchParams.get('cancelled') === 'true') {
            const t = setTimeout(() => {
                toast.info('Плащането беше отказано. Количката ви е запазена — можете да опитате отново.');
            }, 300);
            return () => clearTimeout(t);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Prefill email from user profile
    useEffect(() => {
        if (user?.email) {
            setCustomerEmail(prev => prev || user.email);
        }
    }, [user]);

    // Validation function
    const validateForm = () => {
        const newErrors = {};
        
        // Email is REQUIRED — needed for order confirmation
        if (!customerEmail || !customerEmail.trim()) {
            newErrors.customerEmail = 'Имейл адресът е задължителен, за да получите потвърждение на поръчката.';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim())) {
            newErrors.customerEmail = 'Моля, въведете валиден имейл адрес.';
        }
        
        // Address validation (if new address or guest)
        if (showNewAddress || isGuest) {
            if (!newAddress.name || !newAddress.name.trim()) {
                newErrors.name = 'Името е задължително';
            } else if (newAddress.name.trim().length < 3) {
                newErrors.name = 'Името трябва да е поне 3 символа';
            }
            
            if (!newAddress.phone || !newAddress.phone.trim()) {
                newErrors.phone = 'Телефонът е задължителен';
            } else if (!/^[\+]?[0-9]{9,15}$/.test(newAddress.phone.replace(/\s/g, ''))) {
                newErrors.phone = 'Невалиден телефонен номер';
            }
            
            if (!newAddress.city || !newAddress.city.trim()) {
                newErrors.city = 'Градът е задължителен';
            }
            
            if (deliveryType === 'address' && (!newAddress.address || !newAddress.address.trim())) {
                newErrors.address = 'Адресът е задължителен';
            }
        } else if (!selectedAddressId) {
            newErrors.address = 'Моля изберете адрес за доставка';
        }
        
        // Econt delivery validation
        if (!econtDelivery) {
            newErrors.delivery = 'Моля изберете начин на доставка';
        }
        
        // Payment method validation
        if (!paymentMethod) {
            newErrors.payment = 'Моля изберете начин на плащане';
        } else if (paymentMethod === 'cod' && codBlocked) {
            newErrors.payment = 'Наложен платеж не е достъпен за месни и млечни продукти при доставка извън София';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEcontDeliveryChange = useCallback((deliveryData) => {
        setEcontDelivery(deliveryData);
        setShippingCost(deliveryData?.shippingCost || 0);
        // Update newAddress with Econt data
        if (deliveryData) {
            setNewAddress(prev => ({
                ...prev,
                city: deliveryData.city || prev.city,
                postal_code: deliveryData.postcode || prev.postal_code,
                address: deliveryData.streetAddress || prev.address,
                econt_office: deliveryData.office?.code || ''
            }));
            setDeliveryType(deliveryData.method === 'office' ? 'office' : 'address');
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const bankRes = await axios.get(`${API}/bank-details/active`);
                setBankDetails(bankRes.data);
            } catch (error) {
                console.error('Error fetching bank details:', error);
            }
            if (!token) {
                setShowNewAddress(true);
                return;
            }
            try {
                const addrRes = await axios.get(`${API}/user/addresses`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setAddresses(addrRes.data);
                if (addrRes.data.length > 0) {
                    const defaultAddr = addrRes.data.find(a => a.is_default) || addrRes.data[0];
                    setSelectedAddressId(defaultAddr.address_id);
                } else {
                    setShowNewAddress(true);
                }
            } catch (error) {
                console.error('Error fetching addresses:', error);
                setShowNewAddress(true);
            }
        };
        fetchData();
    }, [token]);

    const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} копиран!`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form before submitting
        if (!validateForm()) {
            toast.error('Моля, попълнете всички задължителни полета');
            return;
        }
        
        setLoading(true);

        try {
            const orderData = {
                payment_method: paymentMethod,
                notes: notes || null,
                shipping_cost: shippingCost,
                econt_delivery: econtDelivery ? {
                    method: econtDelivery.method,
                    city: econtDelivery.city,
                    postcode: econtDelivery.postcode,
                    office_code: econtDelivery.office?.code || null,
                    office_name: econtDelivery.office?.name || null,
                    delivery_days: econtDelivery.deliveryDays
                } : null
            };

            // Optional email — attach only if provided (works for both guests & logged in)
            if (customerEmail && customerEmail.trim()) {
                orderData.guest_email = customerEmail.trim();
            }

            // Add guest checkout data if not logged in
            if (isGuest) {
                // Always include session_id for guest orders
                const currentSessionId = sessionId || localStorage.getItem('cart_session_id');
                if (currentSessionId) {
                    orderData.session_id = currentSessionId;
                }
            }

            if (selectedAddressId && !showNewAddress && !isGuest) {
                orderData.address_id = selectedAddressId;
            } else {
                orderData.address_data = {
                    ...newAddress,
                    delivery_type: deliveryType
                };
            }

            // Set headers - only include token if logged in
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const orderResponse = await axios.post(`${API}/orders`, orderData, { headers });
            const createdOrder = orderResponse.data;

            // Store access token so the success/tracking pages can load full order details
            try {
                if (createdOrder.access_token) {
                    sessionStorage.setItem(`order_access_${createdOrder.order_id}`, createdOrder.access_token);
                }
            } catch (err) { /* sessionStorage unavailable */ }

            if (paymentMethod === 'card') {
                // Create MyPOS checkout session
                let checkoutResponse;
                try {
                    checkoutResponse = await axios.post(
                        `${API}/mypos/create-checkout`,
                        {
                            order_id: createdOrder.order_id,
                            origin_url: window.location.origin
                        },
                        { headers }
                    );
                } catch (mypossErr) {
                    console.error('MyPOS checkout error:', mypossErr);
                    toast.error(`Поръчка №${createdOrder.order_id.replace('ord_', '').toUpperCase()} е приета, но връзката с платежната система не успя. Моля, опитайте отново или изберете друг метод на плащане.`, { duration: 10000 });
                    setLoading(false);
                    return;
                }

                // Create form and submit to MyPOS
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = checkoutResponse.data.checkout_url;
                
                // Add all params as hidden fields
                Object.entries(checkoutResponse.data.params).forEach(([key, value]) => {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = value || '';
                    form.appendChild(input);
                });
                
                document.body.appendChild(form);
                form.submit();
            } else if (paymentMethod === 'bank_transfer') {
                // Bank transfer - show bank details and redirect to success
                toast.success('Поръчката е създадена! Моля, направете банков превод.');
                clearCart();
                navigate(`/order-success?order_id=${createdOrder.order_id}&bank=true`, { state: { order: createdOrder } });
            } else {
                // COD - redirect to success
                toast.success('Поръчката е успешна!');
                clearCart();
                navigate(`/order-success?order_id=${createdOrder.order_id}`, { state: { order: createdOrder } });
            }
        } catch (error) {
            console.error('Error creating order:', error);
            const errorMessage = error.response?.data?.detail || 'Грешка при създаване на поръчката';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Prepayment rule: refrigerated (meat/dairy) products outside Sofia — no COD
    const restrictedItems = cart.items.filter(i => i.requires_prepayment_outside_sofia);
    const effectiveCity = econtDelivery?.city || ((showNewAddress || isGuest) ? newAddress.city : (addresses.find(a => a.address_id === selectedAddressId)?.city || ''));
    const codBlocked = restrictedItems.length > 0 && !!effectiveCity && !isSofiaCity(effectiveCity);

    useEffect(() => {
        if (codBlocked && paymentMethod === 'cod') {
            setPaymentMethod('card');
            toast.info('Наложен платеж не е достъпен за месни и млечни продукти при доставка извън София. Избрано е плащане с карта.');
        }
    }, [codBlocked, paymentMethod]);

    // Don't redirect immediately - wait for cart to load
    if (cartLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Зареждане...</p>
                </div>
            </div>
        );
    }
    
    if (!cartLoading && cart.items.length === 0) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-white flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Количката е празна</h2>
                    <p className="text-gray-600 mb-4">Моля, добавете продукти в количката преди да продължите.</p>
                    <Button onClick={() => navigate('/products')}>
                        Към продуктите
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-white">
            <div className="container-main py-8">
                <Breadcrumbs />
                <h1 className="font-serif text-3xl md:text-4xl font-bold mb-6 text-slate-800">Плащане</h1>
                
                {/* Progress Bar */}
                <CheckoutProgress currentStep={3} />

                <form onSubmit={handleSubmit} noValidate>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Left Column - Forms */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Email - REQUIRED for order confirmation */}
                            <div className="bg-white rounded-lg border-2 border-amber-300 p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                        <Mail className="h-5 w-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <h2 className="font-serif text-xl font-bold text-slate-800">
                                            Имейл адрес <span className="text-red-600">*</span>
                                        </h2>
                                        <p className="text-sm text-muted-foreground">Задължителен — ще получите потвърждение и информация за статуса на поръчката.</p>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Input
                                        id="customer-email"
                                        type="email"
                                        value={customerEmail}
                                        onChange={(e) => {
                                            setCustomerEmail(e.target.value);
                                            if (errors.customerEmail) setErrors(prev => ({...prev, customerEmail: ''}));
                                        }}
                                        placeholder="example@email.com"
                                        className={errors.customerEmail ? 'border-red-500' : ''}
                                        data-testid="checkout-email"
                                        required
                                    />
                                    {errors.customerEmail && <p className="text-red-500 text-sm mt-1" data-testid="checkout-email-error">{errors.customerEmail}</p>}
                                </div>
                            </div>

                            {/* Delivery Address */}
                            <div className="bg-white rounded-lg border border-border p-6">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        <MapPin className="h-5 w-5 text-primary" />
                                    </div>
                                    <h2 className="font-serif text-xl font-bold">Адрес за доставка</h2>
                                </div>

                                {/* Saved Addresses - only for logged in users */}
                                {!isGuest && addresses.length > 0 && !showNewAddress && (
                                    <div className="space-y-3 mb-4">
                                        {addresses.map((addr) => (
                                            <label
                                                key={addr.address_id}
                                                className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                                                    selectedAddressId === addr.address_id 
                                                        ? 'border-secondary bg-secondary/5' 
                                                        : 'border-border hover:border-secondary/50'
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="address"
                                                    value={addr.address_id}
                                                    checked={selectedAddressId === addr.address_id}
                                                    onChange={() => setSelectedAddressId(addr.address_id)}
                                                    className="mt-1"
                                                />
                                                <div>
                                                    <p className="font-medium">{addr.name}</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {addr.address}, {addr.city}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">{addr.phone}</p>
                                                </div>
                                            </label>
                                        ))}
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => setShowNewAddress(true)}
                                            className="w-full"
                                        >
                                            <Plus className="h-4 w-4 mr-2" />
                                            Добави нов адрес
                                        </Button>
                                    </div>
                                )}

                                {/* New Address Form - always shown for guests */}
                                {(isGuest || showNewAddress) && (
                                    <div className="space-y-4">
                                        {!isGuest && addresses.length > 0 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                onClick={() => setShowNewAddress(false)}
                                                className="mb-4"
                                            >
                                                ← Използвай запазен адрес
                                            </Button>
                                        )}

                                        {/* Personal Info */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <Label htmlFor="name">Име и фамилия *</Label>
                                                <Input
                                                    id="name"
                                                    value={newAddress.name}
                                                    onChange={(e) => {
                                                        setNewAddress({ ...newAddress, name: e.target.value });
                                                        if (errors.name) setErrors(prev => ({...prev, name: ''}));
                                                    }}
                                                    required
                                                    className={`mt-1 ${errors.name ? 'border-red-500' : ''}`}
                                                    data-testid="checkout-name"
                                                />
                                                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                                            </div>
                                            <div>
                                                <Label htmlFor="phone">Телефон *</Label>
                                                <Input
                                                    id="phone"
                                                    type="tel"
                                                    inputMode="tel"
                                                    autoComplete="tel"
                                                    value={newAddress.phone}
                                                    onChange={(e) => {
                                                        setNewAddress({ ...newAddress, phone: e.target.value });
                                                        if (errors.phone) setErrors(prev => ({...prev, phone: ''}));
                                                    }}
                                                    required
                                                    className={`mt-1 ${errors.phone ? 'border-red-500' : ''}`}
                                                    data-testid="checkout-phone"
                                                />
                                                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                                            </div>
                                        </div>

                                        {/* Econt Delivery Selector */}
                                        <EcontDeliverySelector 
                                            onDeliveryChange={handleEcontDeliveryChange}
                                            cartTotal={cart.total}
                                            cartWeight={cart.total_weight || 0.5}
                                            defaultCity={newAddress.city}
                                            defaultPostcode={newAddress.postal_code}
                                        />
                                        {errors.delivery && <p className="text-red-500 text-sm mt-1">{errors.delivery}</p>}

                                        {/* Additional address details for address delivery */}
                                        {deliveryType === 'address' && econtDelivery?.city && (
                                            <div className="mt-4">
                                                <Label htmlFor="address">Точен адрес (улица, номер, вход) *</Label>
                                                <Input
                                                    id="address"
                                                    value={newAddress.address}
                                                    onChange={(e) => {
                                                        setNewAddress({ ...newAddress, address: e.target.value });
                                                        if (errors.address) setErrors(prev => ({...prev, address: ''}));
                                                    }}
                                                    required
                                                    placeholder="ул. Витоша 45, вх. А, ет. 3"
                                                    className={`mt-1 ${errors.address ? 'border-red-500' : ''}`}
                                                    data-testid="checkout-address"
                                                />
                                                {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Payment Method */}
                            <div className="bg-white rounded-lg border border-border p-6">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                                        <CreditCard className="h-5 w-5 text-secondary" />
                                    </div>
                                    <h2 className="font-serif text-xl font-bold">Начин на плащане</h2>
                                </div>

                                {codBlocked && (
                                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="prepayment-warning-banner">
                                        <p className="text-sm text-blue-800">
                                            🧊 Поръчката ви съдържа месни или млечни продукти, които се съхраняват в хладилник. При доставка извън София тези продукти се изпращат само след предварително плащане, тъй като непотърсените пратки могат да останат в офис на Еконт и храната да се развали.
                                        </p>
                                    </div>
                                )}

                                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                                    <label onClick={() => { if (!codBlocked) setPaymentMethod('cod'); }} className={`flex items-center gap-3 p-4 border rounded-lg ${
                                        codBlocked ? 'opacity-60 cursor-not-allowed bg-slate-50' : 'cursor-pointer'
                                    } ${
                                        paymentMethod === 'cod' ? 'border-secondary bg-secondary/5' : 'border-border'
                                    }`}>
                                        <RadioGroupItem value="cod" disabled={codBlocked} data-testid="payment-cod" />
                                        <Banknote className="h-5 w-5 text-muted-foreground" />
                                        <div>
                                            <p className="font-medium">Наложен платеж</p>
                                            <p className="text-sm text-muted-foreground">Плащане при доставка</p>
                                            {codBlocked && (
                                                <p className="text-xs text-red-600 mt-1" data-testid="cod-blocked-note">
                                                    Наложен платеж не е достъпен за месни и млечни продукти при доставка извън София.
                                                </p>
                                            )}
                                        </div>
                                    </label>
                                    <label onClick={() => setPaymentMethod('card')} className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer ${
                                        paymentMethod === 'card' ? 'border-secondary bg-secondary/5' : 'border-border'
                                    }`}>
                                        <RadioGroupItem value="card" data-testid="payment-card" />
                                        <CreditCard className="h-5 w-5 text-muted-foreground" />
                                        <div>
                                            <p className="font-medium">Карта</p>
                                            <p className="text-sm text-muted-foreground">Visa, Mastercard, American Express</p>
                                        </div>
                                    </label>
                                    {bankDetails && (
                                        <label onClick={() => setPaymentMethod('bank_transfer')} className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer ${
                                            paymentMethod === 'bank_transfer' ? 'border-secondary bg-secondary/5' : 'border-border'
                                        }`}>
                                            <RadioGroupItem value="bank_transfer" data-testid="payment-bank" />
                                            <Building2 className="h-5 w-5 text-muted-foreground" />
                                            <div>
                                                <p className="font-medium">Банков превод</p>
                                                <p className="text-sm text-muted-foreground">Директен превод по банкова сметка</p>
                                            </div>
                                        </label>
                                    )}
                                </RadioGroup>
                                
                                {/* Bank Details Display */}
                                {paymentMethod === 'bank_transfer' && bankDetails && (
                                    <div className="mt-4 p-4 bg-violet-50 border border-violet-200 rounded-lg">
                                        <p className="font-semibold mb-3 text-violet-800">Банкови данни за превод:</p>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-center justify-between">
                                                <span className="text-muted-foreground">Банка:</span>
                                                <span className="font-medium">{bankDetails.bank_name}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-muted-foreground">Титуляр:</span>
                                                <span className="font-medium">{bankDetails.account_holder}</span>
                                            </div>
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-muted-foreground">IBAN:</span>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono font-medium">{bankDetails.iban}</span>
                                                    <button 
                                                        type="button"
                                                        onClick={() => copyToClipboard(bankDetails.iban, 'IBAN')}
                                                        className="p-1 hover:bg-violet-100 rounded"
                                                    >
                                                        <Copy className="h-4 w-4 text-violet-600" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-muted-foreground">BIC:</span>
                                                <span className="font-mono font-medium">{bankDetails.bic}</span>
                                            </div>
                                        </div>
                                        <p className="mt-3 text-xs text-violet-600">
                                            * Моля, посочете номера на поръчката в основанието за превод
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Notes */}
                            <div className="bg-white rounded-lg border border-border p-6">
                                <Label htmlFor="notes">Бележки към поръчката (незадължително)</Label>
                                <Textarea
                                    id="notes"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Добавете специални инструкции..."
                                    className="mt-2"
                                    data-testid="checkout-notes"
                                />
                            </div>
                        </div>

                        {/* Right Column - Summary */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-lg border border-border p-6 sticky top-32">
                                <h2 className="font-serif text-xl font-bold mb-6">Вашата поръчка</h2>
                                
                                {/* Items */}
                                <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                                    {cart.items.map((item) => (
                                        <div key={item.product_id} className="flex gap-3">
                                            <img 
                                                src={item.image_url} 
                                                alt={item.name}
                                                className="w-16 h-16 object-cover rounded"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm line-clamp-2">{item.name}</p>
                                                <p className="text-sm text-muted-foreground">x{item.quantity}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-medium text-sm">
                                                    {getPriceData(item.price * item.quantity).eurFormatted} €
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="border-t border-border pt-4 space-y-3 mb-6">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Продукти</span>
                                        <div className="text-right">
                                            <span>{getPriceData(cart.total).eurFormatted} €</span>
                                        </div>
                                    </div>
                                    <div className="flex justify-between" data-testid="checkout-shipping-row">
                                        <span className="text-muted-foreground">
                                            {econtDelivery?.method === 'sofia_driver' ? 'Доставка в гр. София с наш шофьор' : 'Доставка (Еконт)'}
                                        </span>
                                        {econtDelivery ? (
                                            shippingCost > 0 ? (
                                                <div className="text-right">
                                                    <span className="text-amber-600">{getPriceData(shippingCost).eurFormatted} €</span>
                                                </div>
                                            ) : (
                                                <span className="text-green-600 font-medium">Безплатна</span>
                                            )
                                        ) : (
                                            <span className="text-muted-foreground italic">Изберете адрес</span>
                                        )}
                                    </div>
                                </div>

                                <div className="border-t border-border pt-4 mb-6">
                                    <div className="flex justify-between text-lg font-bold">
                                        <span>Общо</span>
                                        <div className="text-right">
                                            <span className="text-primary">{getPriceData(cart.total + shippingCost).eurFormatted} €</span>
                                        </div>
                                    </div>
                                    
                                    {/* Minimum order warning */}
                                    {cart.total < MINIMUM_ORDER_AMOUNT && (
                                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                            <p className="text-sm text-amber-800">
                                                ⚠️ Минималната сума за поръчка е <strong>{getPriceData(MINIMUM_ORDER_AMOUNT).eurFormatted} €</strong>
                                            </p>
                                            <p className="text-xs text-amber-600 mt-1">
                                                Добавете още {getPriceData(MINIMUM_ORDER_AMOUNT - cart.total).eurFormatted} € към кошницата
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <Button 
                                    type="submit"
                                    disabled={loading || cart.total < MINIMUM_ORDER_AMOUNT || !econtDelivery || (showNewAddress && (!newAddress.name || !newAddress.phone))}
                                    className="btn-primary w-full"
                                    data-testid="place-order-btn"
                                >
                                    {loading ? 'Обработка...' : paymentMethod === 'card' ? 'Продължи към плащане' : paymentMethod === 'bank_transfer' ? 'Потвърди и виж банкови данни' : 'Потвърди поръчката'}
                                </Button>
                                
                                {/* Validation hints */}
                                {!econtDelivery && (
                                    <p className="text-amber-600 text-sm mt-2 text-center">
                                        ⚠️ Моля, изберете начин на доставка
                                    </p>
                                )}
                                {showNewAddress && (!newAddress.name || !newAddress.phone) && (
                                    <p className="text-amber-600 text-sm mt-2 text-center">
                                        ⚠️ Моля, попълнете име и телефон
                                    </p>
                                )}

                                <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
                                    <Truck className="h-4 w-4" />
                                    <span>
                                        {econtDelivery?.method === 'sofia_driver' 
                                            ? 'Доставка в София с наш шофьор' 
                                            : 'Доставка с Еконт до 3 работни дни'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CheckoutPage;
